/**
 * Secure Login Form Component
 *
 * Provides user authentication interface with security validation,
 * MFA support, and comprehensive error handling.
 *
 * Features:
 * - Email and password validation
 * - Multi-factor authentication support
 * - Rate limiting and brute force protection
 * - Secure input handling and sanitization
 * - Accessibility support
 * - Loading states and error handling
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import MagicLinkLogin from './MagicLinkLogin';
import './LoginPageAnimations.css';
import '../../styles/modern-theme.css';

interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  redirectTo?: string;
}

interface LoginFormState {
  email: string;
  password: string;
  mfaCode: string;
  rememberMe: boolean;
  showMFA: boolean;
  showPassword: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  mfaCode?: string;
  general?: string;
}

interface LoginAttempt {
  timestamp: number;
  email: string;
  success: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onError, redirectTo }) => {
  const { login, isLoading } = useAuth();
  const analytics = useAnalytics();
  const [loginMode, setLoginMode] = useState<'password' | 'magic'>('password');

  const [formState, setFormState] = useState<LoginFormState>({
    email: '',
    password: '',
    mfaCode: '',
    rememberMe: false,
    showMFA: false,
    showPassword: false
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitReset, setRateLimitReset] = useState<Date | null>(null);

  // Refs for security
  const formRef = useRef<HTMLFormElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const mfaRef = useRef<HTMLInputElement>(null);

  // Load login attempts from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('login_attempts');
      if (stored) {
        const attempts = JSON.parse(stored) as LoginAttempt[];
        // Filter attempts from last hour
        const oneHourAgo = Date.now() - 3600000;
        const recentAttempts = attempts.filter(attempt => attempt.timestamp > oneHourAgo);
        setLoginAttempts(recentAttempts);

        // Check if rate limited
        checkRateLimit(recentAttempts);
      }
    } catch (error) {
      console.error('Failed to load login attempts:', error);
    }
  }, []);

  // Validate form inputs
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    // Email validation
    if (!formState.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formState.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formState.password) {
      errors.password = 'Password is required';
    } else if (formState.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    // MFA validation (if required)
    if (formState.showMFA && !formState.mfaCode) {
      errors.mfaCode = 'MFA code is required';
    } else if (formState.showMFA && !/^\d{6}$/.test(formState.mfaCode)) {
      errors.mfaCode = 'MFA code must be 6 digits';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check for rate limiting
  const checkRateLimit = (attempts: LoginAttempt[]): void => {
    const failedAttempts = attempts.filter(attempt => !attempt.success);
    const recentFailures = failedAttempts.filter(
      attempt => Date.now() - attempt.timestamp < 900000 // 15 minutes
    );

    if (recentFailures.length >= 5) {
      setIsRateLimited(true);
      setRateLimitReset(new Date(Date.now() + 900000)); // 15 minutes from now

      // Set timer to reset rate limit
      setTimeout(() => {
        setIsRateLimited(false);
        setRateLimitReset(null);
      }, 900000);
    }
  };

  // Record login attempt
  const recordLoginAttempt = (email: string, success: boolean): void => {
    const attempt: LoginAttempt = {
      timestamp: Date.now(),
      email: email.toLowerCase(),
      success
    };

    const updatedAttempts = [...loginAttempts, attempt];
    setLoginAttempts(updatedAttempts);

    try {
      localStorage.setItem('login_attempts', JSON.stringify(updatedAttempts));
    } catch (error) {
      console.error('Failed to store login attempts:', error);
    }

    if (!success) {
      checkRateLimit(updatedAttempts);
    }
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (isRateLimited) {
      setValidationErrors({ general: 'Too many failed attempts. Please try again later.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const success = await login({
        email: formState.email.toLowerCase().trim(),
        password: formState.password
      });

      if (!success) {
        throw new Error('Login failed');
      }

      // Record successful attempt
      recordLoginAttempt(formState.email, true);

      // Track successful login
      analytics.auth.login('email', true, {
        has_mfa: formState.showMFA,
        remember_me: formState.rememberMe,
      });

      // Clear form
      setFormState({
        email: '',
        password: '',
        mfaCode: '',
        rememberMe: false,
        showMFA: false,
        showPassword: false
      });

      // Call success handler
      if (onSuccess) {
        onSuccess();
      }

      // Redirect if specified
      if (redirectTo) {
        window.location.href = redirectTo;
      }

    } catch (error: any) {
      // Record failed attempt
      recordLoginAttempt(formState.email, false);

      let errorMessage = 'Login failed. Please check your credentials.';
      let errorCode = 'auth_failed';

      if (error.message?.includes('MFA')) {
        setFormState(prev => ({ ...prev, showMFA: true }));
        errorMessage = 'Please enter your MFA code to continue.';
        errorCode = 'mfa_required';
      } else if (error.message?.includes('locked') || error.message?.includes('rate limit')) {
        setIsRateLimited(true);
        errorMessage = 'Account temporarily locked due to multiple failed attempts.';
        errorCode = 'rate_limited';
      } else if (error.message) {
        errorMessage = error.message;
        errorCode = 'auth_failed';
      }

      // Track failed login
      analytics.auth.login('email', false, {
        error_code: errorCode,
        error_message: errorMessage,
        has_mfa: formState.showMFA,
      });

      setValidationErrors({ general: errorMessage });

      if (onError) {
        onError(errorMessage);
      }

      // Clear sensitive fields
      setFormState(prev => ({ ...prev, password: '', mfaCode: '' }));

      // Focus appropriate field
      if (formState.showMFA && mfaRef.current) {
        mfaRef.current.focus();
      } else if (passwordRef.current) {
        passwordRef.current.focus();
      }
    }
  };

  // Handle input changes with sanitization
  const handleInputChange = (field: keyof LoginFormState) => (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    let value: string | boolean = event.target.value;

    // Sanitize input
    if (typeof value === 'string') {
      value = value.replace(/[<>]/g, ''); // Basic XSS prevention
    }

    if (field === 'rememberMe') {
      value = event.target.checked;
    }

    setFormState(prev => ({ ...prev, [field]: value }));

    // Clear field-specific errors
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (): void => {
    setFormState(prev => ({ ...prev, showPassword: !prev.showPassword }));
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Format time remaining for rate limit
  const formatRateLimitTime = (): string => {
    if (!rateLimitReset) return '';
    const remaining = Math.ceil((rateLimitReset.getTime() - Date.now()) / 60000);
    return `${remaining} minute${remaining !== 1 ? 's' : ''}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Geometric Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Data Flow Lines */}
        <div className="absolute top-10 left-10 w-96 h-96 opacity-10">
          <svg viewBox="0 0 400 400" className="animate-pulse">
            <defs>
              <linearGradient id="dataFlow" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="50%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#06b6d4" />
              </linearGradient>
            </defs>
            <path d="M20,200 Q200,50 380,200 T780,200" stroke="url(#dataFlow)" strokeWidth="2" fill="none" strokeDasharray="5,5">
              <animateTransform attributeName="transform" type="translate" values="0,0;20,0;0,0" dur="4s" repeatCount="indefinite" />
            </path>
            <path d="M0,100 Q100,20 200,100 Q300,180 400,100" stroke="url(#dataFlow)" strokeWidth="1.5" fill="none" strokeDasharray="3,3">
              <animateTransform attributeName="transform" type="translate" values="0,0;-10,5;0,0" dur="6s" repeatCount="indefinite" />
            </path>
            <path d="M0,300 Q100,220 200,300 Q300,380 400,300" stroke="url(#dataFlow)" strokeWidth="1" fill="none" strokeDasharray="2,4">
              <animateTransform attributeName="transform" type="translate" values="0,0;15,-5;0,0" dur="5s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>

        {/* Floating Hexagonal Network Nodes */}
        <div className="absolute top-20 right-20 w-64 h-64 opacity-20">
          <svg viewBox="0 0 200 200" className="animate-spin" style={{ animationDuration: '20s' }}>
            <defs>
              <polygon id="hexagon" points="30,15 50,5 70,15 70,35 50,45 30,35" fill="#3b82f6" opacity="0.6" />
              <polygon id="hexagon-small" points="15,8 23,4 31,8 31,16 23,20 15,16" fill="#8b5cf6" opacity="0.8" />
            </defs>
            <use href="#hexagon" x="0" y="0" />
            <use href="#hexagon" x="40" y="30" />
            <use href="#hexagon" x="80" y="10" />
            <use href="#hexagon-small" x="20" y="50" />
            <use href="#hexagon-small" x="60" y="70" />
            <use href="#hexagon-small" x="100" y="40" />
            <path d="M45,25 L65,45 M85,25 L105,45 M35,65 L55,85" stroke="#06b6d4" strokeWidth="1" opacity="0.4" strokeDasharray="2,2">
              <animate attributeName="stroke-dashoffset" values="0;4;0" dur="3s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>

        {/* Circuit Board Pattern */}
        <div className="absolute bottom-10 left-20 w-80 h-80 opacity-10">
          <svg viewBox="0 0 300 300" className="animate-pulse">
            <defs>
              <pattern id="circuit" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                <rect width="30" height="30" fill="none" stroke="#3b82f6" strokeWidth="0.5" opacity="0.3" />
                <circle cx="15" cy="15" r="2" fill="#8b5cf6" opacity="0.6" />
                <line x1="0" y1="15" x2="30" y2="15" stroke="#06b6d4" strokeWidth="0.5" opacity="0.4" />
                <line x1="15" y1="0" x2="15" y2="30" stroke="#06b6d4" strokeWidth="0.5" opacity="0.4" />
              </pattern>
            </defs>
            <rect width="300" height="300" fill="url(#circuit)" />
          </svg>
        </div>

        {/* Metrics Visualization Shapes */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 opacity-15">
          <svg viewBox="0 0 100 100" className="animate-bounce" style={{ animationDuration: '3s' }}>
            <rect x="10" y="60" width="8" height="30" fill="#3b82f6" rx="2">
              <animate attributeName="height" values="30;45;30" dur="2s" repeatCount="indefinite" />
            </rect>
            <rect x="25" y="45" width="8" height="45" fill="#8b5cf6" rx="2">
              <animate attributeName="height" values="45;60;45" dur="2.5s" repeatCount="indefinite" />
            </rect>
            <rect x="40" y="30" width="8" height="60" fill="#06b6d4" rx="2">
              <animate attributeName="height" values="60;75;60" dur="3s" repeatCount="indefinite" />
            </rect>
            <rect x="55" y="40" width="8" height="50" fill="#10b981" rx="2">
              <animate attributeName="height" values="50;70;50" dur="2.2s" repeatCount="indefinite" />
            </rect>
            <rect x="70" y="55" width="8" height="35" fill="#f59e0b" rx="2">
              <animate attributeName="height" values="35;50;35" dur="1.8s" repeatCount="indefinite" />
            </rect>
          </svg>
        </div>

        {/* Signal Wave Pattern */}
        <div className="absolute bottom-1/4 right-1/4 w-40 h-20 opacity-20">
          <svg viewBox="0 0 160 80" className="animate-pulse">
            <path d="M0,40 Q20,20 40,40 T80,40 T120,40 T160,40" stroke="#06b6d4" strokeWidth="2" fill="none">
              <animate attributeName="d" values="M0,40 Q20,20 40,40 T80,40 T120,40 T160,40;M0,40 Q20,60 40,40 T80,40 T120,40 T160,40;M0,40 Q20,20 40,40 T80,40 T120,40 T160,40" dur="2s" repeatCount="indefinite" />
            </path>
            <path d="M0,50 Q20,30 40,50 T80,50 T120,50 T160,50" stroke="#8b5cf6" strokeWidth="1.5" fill="none" opacity="0.7">
              <animate attributeName="d" values="M0,50 Q20,30 40,50 T80,50 T120,50 T160,50;M0,50 Q20,70 40,50 T80,50 T120,50 T160,50;M0,50 Q20,30 40,50 T80,50 T120,50 T160,50" dur="2.5s" repeatCount="indefinite" />
            </path>
            <path d="M0,60 Q20,40 40,60 T80,60 T120,60 T160,60" stroke="#3b82f6" strokeWidth="1" fill="none" opacity="0.5">
              <animate attributeName="d" values="M0,60 Q20,40 40,60 T80,60 T120,60 T160,60;M0,60 Q20,80 40,60 T80,60 T120,60 T160,60;M0,60 Q20,40 40,60 T80,60 T120,60 T160,60" dur="3s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>
      </div>

      {/* Mobile Header - Visible on small screens */}
      <div className="lg:hidden text-center mb-8 px-4 pt-8 relative z-10">
        <h1 className="text-3xl font-bold mb-3 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          AI Telemetry Dashboard
        </h1>
        <p className="text-slate-300 text-sm leading-relaxed max-w-md mx-auto">
          Intelligent monitoring and analytics for AI systems
        </p>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-col lg:flex-row w-full max-w-6xl mx-auto relative z-10">
        {/* Left Side - Descriptive Content (Desktop Only) */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 xl:p-12 text-white bg-slate-800/30 backdrop-blur border-r border-slate-700/50">
          <div className="mb-8">
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              AI Telemetry Dashboard
            </h1>
            <p className="text-xl text-slate-100 mb-6 leading-relaxed font-light">
              Harness the power of intelligent monitoring and analytics to optimize your AI systems with real-time insights and predictive intelligence.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="text-slate-100 font-medium">Real-time Performance Monitoring</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-slate-100 font-medium">Predictive Analytics & Insights</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <span className="text-slate-100 font-medium">Intelligent System Optimization</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <span className="text-slate-100 font-medium">Enterprise-Grade Security</span>
              </div>
            </div>

            <div className="text-sm text-slate-300">
              <p className="font-medium">Trusted by leading AI companies worldwide</p>
              <p className="mt-2">✨ <span className="font-semibold text-white">99.9%</span> uptime • 🚀 <span className="font-semibold text-white">Real-time</span> processing • 🔒 <span className="font-semibold text-white">SOC 2</span> compliant</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-md">
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl shadow-2xl p-6 sm:p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">Welcome Back</h2>
                <p className="text-slate-300 mt-2 text-sm sm:text-base">Sign in to access your telemetry dashboard</p>
              </div>

        {/* Login Mode Toggle */}
        <div className="flex bg-slate-700/50 rounded-lg p-1 mb-6">
          <button
            type="button"
            onClick={() => setLoginMode('password')}
            className={`flex-1 py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              loginMode === 'password'
                ? 'bg-slate-600 text-white shadow-md border border-slate-500'
                : 'text-slate-300 hover:text-white hover:bg-slate-600/50'
            }`}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => setLoginMode('magic')}
            className={`flex-1 py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              loginMode !== 'password'
                ? 'bg-slate-600 text-white shadow-md border border-slate-500'
                : 'text-slate-300 hover:text-white hover:bg-slate-600/50'
            }`}
          >
            Magic Link
          </button>
        </div>

        {loginMode === 'magic' ? (
          <MagicLinkLogin onSuccess={onSuccess} onError={onError} redirectTo={redirectTo} />
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* General Error */}
          {validationErrors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{validationErrors.general}</p>
              {isRateLimited && rateLimitReset && (
                <p className="text-red-600 text-xs mt-1">
                  Try again in {formatRateLimitTime()}
                </p>
              )}
            </div>
          )}

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
              Email Address
            </label>
            <input
              ref={emailRef}
              type="email"
              id="email"
              name="email"
              value={formState.email}
              onChange={handleInputChange('email')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-slate-700/50 text-white placeholder-slate-400 ${
                validationErrors.email ? 'border-red-400 bg-red-900/20' : 'border-slate-600 hover:border-slate-500'
              }`}
              placeholder="you@company.com"
              autoComplete="email"
              disabled={isLoading || isRateLimited}
              aria-describedby={validationErrors.email ? 'email-error' : undefined}
              required
            />
            {validationErrors.email && (
              <p id="email-error" className="text-red-600 text-sm mt-1">
                {validationErrors.email}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-200 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                ref={passwordRef}
                type={formState.showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formState.password}
                onChange={handleInputChange('password')}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-slate-700/50 text-white placeholder-slate-400 ${
                  validationErrors.password ? 'border-red-400 bg-red-900/20' : 'border-slate-600 hover:border-slate-500'
                }`}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLoading || isRateLimited}
                aria-describedby={validationErrors.password ? 'password-error' : undefined}
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-200"
                disabled={isLoading || isRateLimited}
                aria-label={formState.showPassword ? 'Hide password' : 'Show password'}
              >
                {formState.showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L12 12m6.121-3.121A9.97 9.97 0 0121 12c0 .8-.126 1.57-.36 2.29m-2.065 2.065A10.05 10.05 0 0112 19" />
                  </svg>
                )}
              </button>
            </div>
            {validationErrors.password && (
              <p id="password-error" className="text-red-600 text-sm mt-1">
                {validationErrors.password}
              </p>
            )}
          </div>

          {/* MFA Field (conditional) */}
          {formState.showMFA && (
            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-2">
                Authentication Code
              </label>
              <input
                ref={mfaRef}
                type="text"
                id="mfaCode"
                name="mfaCode"
                value={formState.mfaCode}
                onChange={handleInputChange('mfaCode')}
                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  validationErrors.mfaCode ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="123456"
                maxLength={6}
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                disabled={isLoading || isRateLimited}
                aria-describedby={validationErrors.mfaCode ? 'mfa-error' : undefined}
                required
              />
              {validationErrors.mfaCode && (
                <p id="mfa-error" className="text-red-600 text-sm mt-1">
                  {validationErrors.mfaCode}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
          )}

          {/* Remember Me */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formState.rememberMe}
              onChange={handleInputChange('rememberMe')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isLoading || isRateLimited}
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-slate-300">
              Keep me signed in for 30 days
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || isRateLimited}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-slate-600 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing In...
              </div>
            ) : isRateLimited ? (
              'Rate Limited'
            ) : (
              'Sign In'
            )}
          </button>
          </form>
        )}

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <a
            href="/forgot-password"
            className="text-sm text-blue-600 hover:text-blue-500 underline"
          >
            Forgot your password?
          </a>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-slate-400">
            Don't have an account?{' '}
            <a href="/signup" className="text-blue-400 hover:text-blue-300 underline">
              Contact your administrator
            </a>
          </p>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            🔒 Your connection is secured with 256-bit SSL encryption
          </p>
        </div>
      </div>
    </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;